// frontend/src/pages/adolescentes/AdolescenteMovimentacaoFormPage.jsx
import { useEffect, useState, useMemo, useRef } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import api from '../../services/api';
import LoadingSpinner from '../../components/common/LoadingSpinner';
import MotivosMbaSelector from '../../components/common/MotivosMbaSelector';

function AdolescenteMovimentacaoFormPage() {
  const { id: adolescenteId } = useParams();
  const navigate = useNavigate();

  // Evita double-fetch em dev/StrictMode
  const fetchedRef = useRef(false);
  const abortRef = useRef(null);

  // Header
  const [adolescente, setAdolescente] = useState(null);

  // Form
  const [formData, setFormData] = useState({
    situacao_id: '',
    meio_entrada_id: '',
    motivos_mba_ids: [],
  });

  // Combos
  const [situacoes, setSituacoes] = useState([]);
  const [meiosEntrada, setMeiosEntrada] = useState([]);
  const [motivosMba, setMotivosMba] = useState([]);

  // UI state
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);
  const [validationErrors, setValidationErrors] = useState({});

  useEffect(() => {
    if (fetchedRef.current) return;            // bloqueia repetição
    fetchedRef.current = true;

    const controller = new AbortController();
    abortRef.current = controller;

    const fetchPrereqs = async () => {
      setLoading(true);
      setError(null);
      try {
        const [adolResp, meiosResp, sitsResp, motivosResp] = await Promise.all([
          api.get(`/adolescentes/${adolescenteId}`, { signal: controller.signal }),
          api.get('/meios-entrada', {
            signal: controller.signal,
            params: {
              per_page: 10000,
              status: 'active',
            },
          }),
          api.get('/situacoes', { signal: controller.signal }),
          api.get('/motivos-mba', {
            signal: controller.signal,
            params: {
              per_page: 10000,
              status: 'active',
            },
          }),
        ]);

        setAdolescente(adolResp.data?.data || null);
        setMeiosEntrada(meiosResp.data?.data || []);
        setMotivosMba(motivosResp.data?.data || []);

        // Apenas situações do tipo 'movimentacao'
        const sits = (sitsResp.data?.data || []).filter((s) => s.tipo === 'movimentacao');
        setSituacoes(sits);
      } catch (err) {
        if (controller.signal.aborted) return;

        // Tratamento amigável + silenciar console
        if (err.response?.status === 403) {
          err.userMessage = 'Acesso negado. Você não tem permissão para iniciar movimentação.';
          err.silence = true;
        }
        setError(err.userMessage || 'Não foi possível carregar os dados para iniciar a movimentação.');
        if (!err.silence) console.error(err);
      } finally {
        if (!controller.signal.aborted) setLoading(false);
      }
    };

    fetchPrereqs();

    return () => {
      controller.abort();
    };
  }, [adolescenteId]);

  // MBA selecionado
  const isMBA = useMemo(() => formData.meio_entrada_id === '3', [formData.meio_entrada_id]);
  // Flagrante + MBA selecionado
  const isFlagranteMBA = useMemo(() => formData.meio_entrada_id === '4', [formData.meio_entrada_id]);

  const handleChange = (e) => {
    const { name, value } = e.target;

    if (name === 'meio_entrada_id') {
      setFormData(prev => ({
        ...prev,
        meio_entrada_id: value,
        ...(value !== '3' && value !== '4' ? { motivos_mba_ids: [] } : {})
      }));

      setValidationErrors(prev => {
        const next = { ...prev };
        delete next[name];
        if (value !== '3' && value !== '4') delete next.motivos_mba_ids;
        return next;
      });
      return;
    }

    setFormData(prev => ({ ...prev, [name]: value }));
    if (validationErrors[name]) {
      setValidationErrors(prev => {
        const next = { ...prev };
        delete next[name];
        return next;
      });
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);
    setError(null);
    setValidationErrors({});

    if (!formData.situacao_id || !formData.meio_entrada_id) {
      setError('Preencha os campos obrigatórios: Situação e Meio de Entrada.');
      setSaving(false);
      return;
    }

    const meioSelecionado = meiosEntrada.find(m => m?.id === Number(formData.meio_entrada_id));
    const meioEntradaId = meioSelecionado?.id;

    if (!meioEntradaId) {
      setError('Selecione um Meio de Entrada válido.');
      setSaving(false);
      return;
    }

    const payload = {
      meio_entrada_id: Number(meioEntradaId),
      motivos_mba_ids: (isMBA || isFlagranteMBA) ? formData.motivos_mba_ids : [],
    };

    try {
      const resp = await api.post(`/adolescentes/${adolescenteId}/movimentacoes`, payload);
      const novoId = resp?.data?.data?.id;
      if (!novoId) throw new Error('Resposta inesperada do servidor: ID da movimentação não retornado.');

      await api.post(`/movimentacoes/${novoId}/situacoes`, { situacao_id: Number(formData.situacao_id) });

      // Redireciona para o Dashboard após criar a movimentação
      navigate('/painel/dashboard');
    } catch (err) {
      if (err.response?.status === 403) {
        err.userMessage = 'Acesso negado. Você não tem permissão para criar movimentações.';
        err.silence = true;
      }

      if (err.response?.status === 422) {
        setError('Por favor, corrija os erros no formulário.');
        setValidationErrors(err.response.data.errors || {});
      } else {
        setError(err.userMessage || err.response?.data?.message || 'Ocorreu um erro ao salvar a movimentação.');
      }

      if (!err.silence) console.error(err);
    } finally {
      setSaving(false);
    }
  };

  if (loading) return <LoadingSpinner text="Carregando pré-requisitos..." />;

  return (
    <div className="adolescente-movimentacao-form-page">
      <div className="content-header">
        <h1 className="content-title">Iniciar Movimentação</h1>
        <p className="content-subtitle">
          Adolescente: <strong>{adolescente?.nome_adolescente || '—'}</strong>
        </p>
      </div>

      <div className="card">
        <div className="card-body">
          <form onSubmit={handleSubmit} noValidate>
            {error && <div className="alert alert-danger">{error}</div>}

            <div className="row">
              {/* Situação (OBRIGATÓRIA) */}
              <div className="col-md-6 mb-3">
                <label htmlFor="situacao_id" className="form-label">
                  Selecione a Situação <span className="text-danger">*</span>
                </label>
                <select
                  id="situacao_id"
                  name="situacao_id"
                  className={`form-select ${validationErrors.situacao_id ? 'is-invalid' : ''}`}
                  value={formData.situacao_id}
                  onChange={handleChange}
                  required
                >
                  <option value="" disabled>Selecione...</option>
                  {situacoes.map((s) => (
                    <option key={s.id} value={s.id}>{s.nome}</option>
                  ))}
                </select>
                {validationErrors.situacao_id && (
                  <div className="invalid-feedback">
                    {validationErrors.situacao_id.join(', ')}
                  </div>
                )}
              </div>

              {/* Meio de entrada (OBRIGATÓRIO) */}
              <div className="col-md-6 mb-3">
                <label htmlFor="meio_entrada_id" className="form-label">
                  Selecione o Meio de Entrada <span className="text-danger">*</span>
                </label>
                <select
                  id="meio_entrada_id"
                  name="meio_entrada_id"
                  className={`form-select ${validationErrors.meio_entrada_id ? 'is-invalid' : ''}`}
                  value={formData.meio_entrada_id}
                  onChange={handleChange}
                  required
                >
                  <option value="" disabled>Selecione...</option>
                  {meiosEntrada.map((m) => (
                    <option key={m.id} value={m.id}>{m.nome}</option>
                  ))}
                </select>
                {validationErrors.meio_entrada_id && (
                  <div className="invalid-feedback">
                    {validationErrors.meio_entrada_id.join(', ')}
                  </div>
                )}
              </div>

              {/* Motivo MBA — aparece quando MBA ou Flagrante + MBA */}
              {(isMBA || isFlagranteMBA) && (
                <div className="col-12 col-lg-6 mb-3">
                  <label htmlFor="motivos_mba_ids" className="form-label">
                    Selecione o(s) Motivo(s) MBA <span className="text-danger">*</span>
                  </label>
                  <MotivosMbaSelector
                    id="motivos_mba_ids"
                    motivos={motivosMba}
                    selectedIds={formData.motivos_mba_ids}
                    onChange={(motivosMbaIds) => {
                      setFormData((prev) => ({ ...prev, motivos_mba_ids: motivosMbaIds }));
                      setValidationErrors((prev) => {
                        const next = { ...prev };
                        delete next.motivos_mba_ids;
                        return next;
                      });
                    }}
                    required
                    allowEmptySelection
                    invalid={Boolean(validationErrors.motivos_mba_ids)}
                  />
                  {validationErrors.motivos_mba_ids && (
                    <div className="invalid-feedback">
                      {validationErrors.motivos_mba_ids.join(', ')}
                    </div>
                  )}
                </div>
              )}
            </div>

            <div className="d-flex justify-content-end mt-4">
              <button
                type="button"
                className="btn btn-secondary me-2"
                onClick={() => navigate(-1)}
                disabled={saving}
              >
                Cancelar
              </button>
              <button
                type="submit"
                className="btn btn-primary"
                disabled={
                  saving ||
                  !formData.meio_entrada_id ||
                  !formData.situacao_id ||
                  ((isMBA || isFlagranteMBA) && formData.motivos_mba_ids.length === 0)
                }
              >
                {saving ? 'Salvando...' : 'Continuar'}
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
}

export default AdolescenteMovimentacaoFormPage;
